import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { useToast } from '@/hooks/use-toast';
import { validarCNPJ, mapearDadosCNPJ, type CNPJData } from '@/utils/cnpj';

interface CNPJLookupResult {
  data: ReturnType<typeof mapearDadosCNPJ>;
  /**
   * Payload bruto da Receita Federal (CNPJ.ws). Opcional porque
   * setManualData pode ser chamado com fallbacks locais que não têm
   * payload original. Quando presente, parseCnpjPayload destrava TODOS
   * os campos (capital social, Simples/MEI, inscrições estaduais,
   * regimes tributários etc.) no ReceitaFederalDetails.
   */
  rawPayload?: CNPJData;
  source: 'cache' | 'api';
  atualizado_em: string;
}

export function useCNPJLookup() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CNPJLookupResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const { config } = useSystemConfig();
  const { toast } = useToast();

  const consultarCNPJ = async (cnpj: string) => {
    console.log('🔍 [useCNPJLookup] Iniciando consulta CNPJ:', cnpj);

    // ✅ Buscar token do banco centralizado
    let currentToken: string | null = null;
    
    try {
      const { data: configData, error } = await supabase
        .from('configuracoes_sistema')
        .select('valor')
        .eq('chave', 'integracoes.cnpj_ws.token')
        .single();

      if (!error && configData?.valor) {
        // Token do banco (formato JSONB string)
        currentToken = typeof configData.valor === 'string' ? 
          JSON.parse(configData.valor) : 
          configData.valor;
        
        console.log('✅ [useCNPJLookup] Token obtido do banco:', {
          hasToken: !!currentToken,
          tokenLength: currentToken?.length || 0
        });
      }
    } catch (error) {
      console.error('❌ [useCNPJLookup] Erro ao buscar token do banco:', error);
    }

    // Fallback: tentar localStorage (migração temporária)
    if (!currentToken) {
      console.log('⚠️ [useCNPJLookup] Token não encontrado no banco, tentando localStorage...');
      currentToken = localStorage.getItem('prime_cnpj_ws_token');
    }

    // ✅ Se NÃO TEM token, mostrar aviso e parar
    if (!currentToken || currentToken.trim() === '') {
      console.error('❌ [useCNPJLookup] Token CNPJ.ws não encontrado');
      toast({
        title: "⚠️ Token CNPJ.ws não configurado",
        description: "Peça ao administrador para configurar o token em Configurações → Sistema",
        variant: "destructive",
        duration: 10000
      });
      return null;
    }

    if (!validarCNPJ(cnpj)) {
      setError('CNPJ inválido');
      toast({
        title: "CNPJ inválido",
        description: "Verifique se o CNPJ foi digitado corretamente",
        variant: "destructive"
      });
      return null;
    }

    setLoading(true);
    setError(null);
    setData(null);

    try {
      // ✅ Usar token do localStorage diretamente
      console.log('📡 Iniciando consulta CNPJ:', {
        cnpj,
        hasToken: !!currentToken
      });

      const { data: result, error: funcError } = await supabase.functions.invoke('consultar-cnpj', {
        body: {
          cnpj,
          token: currentToken
        }
      });

      console.log('📥 Resposta da edge function:', { 
        hasResult: !!result, 
        hasError: !!funcError,
        source: result?.source 
      });

      if (funcError) {
        console.error('❌ Erro na edge function:', funcError);
        throw new Error(funcError.message || 'Erro na consulta CNPJ');
      }

      if (result.not_found) {
        console.warn('⚠️ CNPJ não encontrado na Receita Federal — caller decidirá o fallback');
        return { not_found: true } as any;
      }

      if (result.error) {
        console.error('❌ Erro retornado pela API:', result.error);
        throw new Error(result.error);
      }

      const cnpjData: CNPJData = result.data;
      const mappedData = mapearDadosCNPJ(cnpjData);
      
      console.log('✅ Dados CNPJ mapeados:', {
        cnpj: mappedData.cnpj,
        razaoSocial: mappedData.razao_social,
        endereco_rua: mappedData.endereco_rua,
        endereco_numero: mappedData.endereco_numero,
        temEndereco: !!(mappedData.endereco_rua && mappedData.endereco_numero),
        source: result.source
      });
      
      const lookupResult: CNPJLookupResult = {
        data: mappedData,
        rawPayload: cnpjData,
        source: result.source,
        atualizado_em: result.atualizado_em
      };

      setData(lookupResult);
      
      toast({
        title: "CNPJ consultado com sucesso",
        description: `Dados obtidos ${result.source === 'cache' ? 'do cache' : 'da API CNPJ.ws'}`,
      });

      return lookupResult;

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
      
      toast({
        title: "Erro na consulta CNPJ",
        description: errorMessage,
        variant: "destructive"
      });
      
      return null;
    } finally {
      setLoading(false);
    }
  };

  const limparResultado = () => {
    setData(null);
    setError(null);
  };

  // Permite injetar dados manualmente (ex: fallback local do CRM)
  const setManualData = (manualData: CNPJLookupResult) => {
    setData(manualData);
  };

  const checkHasValidToken = async () => {
    try {
      const { data } = await supabase
        .from('configuracoes_sistema')
        .select('valor')
        .eq('chave', 'integracoes.cnpj_ws.token')
        .single();
      
      const token = data?.valor ? 
        (typeof data.valor === 'string' ? JSON.parse(data.valor) : data.valor) : 
        null;
      
      return !!token && token.trim() !== '';
    } catch {
      // Fallback para localStorage
      return !!localStorage.getItem('prime_cnpj_ws_token');
    }
  };

  return {
    consultarCNPJ,
    limparResultado,
    setManualData,
    loading,
    data,
    error,
    hasValidToken: config.cnpjToken !== null && config.cnpjToken !== '',
    checkHasValidToken
  };
}